<template>
  <transition appear name="fade">
    <div
      class="relative flex flex-col h-full bg-white border-2 border-white rounded shadow-lg font-condensed t3m-ObjectiveFinder-listItem isolate md:rounded-lg ring-1 ring-t3m-border-color"
      :class="{
        'is-showing-details border-inherit': isSelected,
        'is-compared': isCompared,
      }"
      style="animation-duration: 1s"
    >
      <header
        class="flex overflow-hidden leading-none rounded-t t3m-ObjectiveFinder-listItem-header md:rounded-t-lg"
      >
        <h3
          class="p-2 mb-0 text-sm leading-none bg-gray-200 mr-2px t3m-ObjectiveFinder-listItem-title grow"
        >
          {{ specs.objectiveName }}
        </h3>
        <span
          class="p-2 text-sm leading-none bg-gray-200 font-condensed t3m-ObjectiveFinder-listItem-application shrink-0 text-t3m-muted"
          >#{{ specs.matnrShort }}</span
        >
      </header>

      <section
        class="flex flex-col w-full t3m-ObjectiveFinder-listItem-body grow"
      >
        <!-- #1 -->
        <div
          class="flex flex-wrap text-lg leading-none divide-x-2 divide-gray-200 t3m-ObjectiveFinder-listItem-row t3m-ObjectiveFinder-listItem-row--1 shrink-0"
        >
          <div
            class="relative flex-1 px-1 py-2 t3m-ObjectiveFinder-listItem-cell t3m-ObjectiveFinder-listItem-cell--magnification"
          >
            <div
              class="text-xl font-bold t3m-ObjectiveFinder-listItem-cell-value"
            >
              {{ magnificationValue(specs) }}
            </div>
            <div
              class="mt-1 text-xs t3m-ObjectiveFinder-listItem-cell-title text-t3m-muted"
            >
              Mag
            </div>
          </div>

          <div
            class="relative flex-1 px-1 py-2 t3m-ObjectiveFinder-listItem-cell t3m-ObjectiveFinder-listItem-cell--aperture"
          >
            <div
              class="text-xl font-bold t3m-ObjectiveFinder-listItem-cell-value"
            >
              {{ specs.aperture }}
            </div>
            <div
              class="mt-1 text-xs t3m-ObjectiveFinder-listItem-cell-title text-t3m-muted"
            >
              NA
            </div>
          </div>

          <div
            class="relative flex-1 px-1 py-2 t3m-ObjectiveFinder-listItem-cell t3m-ObjectiveFinder-listItem-cell--distance"
          >
            <div
              class="text-xl font-bold t3m-ObjectiveFinder-listItem-cell-value"
            >
              {{ specs.freeWorkingDistance }}
            </div>
            <div
              class="mt-1 text-xs t3m-ObjectiveFinder-listItem-cell-title text-t3m-muted"
            >
              WD (mm)
            </div>
          </div>

          <div
            class="relative px-1 py-2 w-2/5 aspect-curve t3m-ObjectiveFinder-listItem-cell t3m-ObjectiveFinder-listItem-cell--transmission"
          >
            <img
              :src="specs.transmissionCurvePicturePath"
              class="cursor-pointer "
              alt
              @click="toggleDetails(1)"
            />
          </div>
        </div>

        <!-- #2 -->
        <div
          class="grid grid-cols-4 gap-1 px-1 text-xs leading-none uppercase t3m-ObjectiveFinder-listItem-row t3m-ObjectiveFinder-listItem-row--2 shrink-0"
        >
          <div
            v-for="(value, index) in specs.cardLabels"
            :key="index"
            class="py-1 text-center border t3m-ObjectiveFinder-listItem-cell t3m-ObjectiveFinder-listItem-cell--contrast px-2px text-t3m-muted border-t3m-border-color"
          >
            <span class="t3m-ObjectiveFinder-listItem-cell-value">{{
              value
            }}</span>
          </div>
        </div>

        <!-- #3 -->
        <div
          class="flex items-end justify-end mt-auto t3m-ObjectiveFinder-listItem-row t3m-ObjectiveFinder-listItem-row--3 shrink-0"
        >
          <div
            v-if="techniques.length"
            class="relative flex-1 px-3 pt-2 pb-3 t3m-ObjectiveFinder-listItem-cell t3m-ObjectiveFinder-listItem-cell--techniques"
          >
            <div
              class="mb-1 text-xs leading-none t3m-ObjectiveFinder-listItem-cell-title text-t3m-muted"
            >
              {{ translations.Techniques }}
            </div>
            <div
              class="text-xl font-bold t3m-ObjectiveFinder-listItem-cell-value"
            >
              {{ techniques }}
            </div>
          </div>
          <div
            class="t3m-ObjectiveFinder-listItem-cell t3m-ObjectiveFinder-listItem-cell--image w-2/5 aspect-square flex items-center justify-center saturate-50"
          >
            <img
              :src="specs.picturePath"
              class="cursor-pointer"
              alt
              @click="toggleDetails()"
            />
          </div>
        </div>
      </section>

      <footer
        class="flex flex-wrap items-center justify-center p-2 mt-auto space-x-2 bg-gray-200 rounded-b t3m-ObjectiveFinder-listItem-footer md:rounded-b-lg md:flex-nowrap"
      >
        <button
          v-if="shouldShowShopButton"
          class="btn btn-xs btn-primary whitespace-nowrap min-w-[28%]"
          @click="toggleDetails(0)"
        >
          {{ buyButtonLabel }}
        </button>
        <button
          class="btn btn-xs btn-outline-secondary whitespace-nowrap min-w-[28%]"
          :class="{ active: isSelected }"
          @click="toggleDetails(0)"
        >
          {{ detailButtonLabel }}
        </button>
        <button
          class="btn btn-xs btn-outline-secondary whitespace-nowrap min-w-[28%]"
          :class="{ active: isCompared }"
          @click="toggleCompare()"
        >
          {{ compareButtonLabel }}
        </button>
      </footer>
    </div>
  </transition>
</template>

<script>
import { valueFormatters } from '../shared'
import { mapState, mapActions } from 'vuex'

export default {
  name: 'Objective',
  props: {
    specs: {
      type: Object,
      default: () => {},
    },
    isLastInList: {
      type: Boolean,
      default: false,
    },
  },
  mixins: [valueFormatters],

  computed: {
    ...mapState([
      'contrastMethodSets',
      'objectivesToCompare',
      'selectedObjective',
      'translations',
      'shopEnabled',
    ]),
    techniques: function() {
      return this.specs.techniquesFilter.join(', ')
    },
    contrastMethodSet: function() {
      return this.contrastMethodSets[this.specs.contrastMethodSet]
    },
    buyButtonLabel: function() {
      return this.translations.Buy || 'Buy now'
    },
    compareButtonLabel: function() {
      return this.isCompared
        ? this.translations.Remove
        : this.translations.Compare
    },
    detailButtonLabel: function() {
      return this.isSelected
        ? this.translations.Close
        : this.translations.Details
    },
    isSelected: function() {
      return (
        this.selectedObjective && this.selectedObjective.uid === this.specs.uid
      )
    },
    isCompared: function() {
      return this.objectivesToCompare.includes(this.specs)
    },
    isShopProduct() {
      return Boolean(
        (this.specs &&
          this.specs.availableonline &&
          this.specs.availableonline.length) ||
          (this.specs &&
            this.specs.availabletopurchase &&
            this.specs.availabletopurchase.length)
      )
    },
    shouldShowShopButton() {
      return this.shopEnabled && this.isShopProduct
    },
  },
  methods: {
    ...mapActions([
      'selectObjective',
      'addObjectiveToCompare',
      'removeObjectiveToCompare',
      'setObjectiveColumnWidth',
      'setListOutOfView',
    ]),
    toggleDetails(targetTabIndex = 0) {
      if (
        this.selectedObjective &&
        this.selectedObjective.uid === this.specs.uid
      ) {
        this.selectObjective({
          objectiveComponent: undefined,
          targetTabIndex: 0,
        })
      } else {
        this.selectObjective({
          objectiveComponent: this,
          targetTabIndex: targetTabIndex,
        })
      }
    },
    toggleCompare() {
      if (this.isCompared) {
        this.removeObjectiveToCompare(this.specs)
      } else {
        this.addObjectiveToCompare(this.specs)
      }
    },
    hasContrastMethod(cm) {
      return this.specs.contrastMethods.includes(cm)
    },
  },
}
</script>
